import {callCaleAccess, callCaleEntitlements, callCaleTextSubscribe, callCaleLogError} from './paywall/call_cale';
import {setupAnalyticsListeners, logPrivateMode, pianoCallbacks} from './paywall/analytics';
import PianoAdBlock from './piano_ad_block';
import isPrivateMode from './private_detection';
import {pianoKey, pianoEnabled, fbEnabled} from './affiliate_settings.js';
import {getUser} from '@adi/gigya_js/src/auth';
import {locationDetails} from '@adi/location-details';
import scriptInjector from '@adi/script-injector';
import pageQuery from '@adi/page-query';
import state from './state';

const loadScript = scriptInjector();

// flags
let refreshAfterLogin = false;
const landingPageId = "piano";

// redirects back tfo facebook via their account linking token and the subscription payload
const redirectToFB = (subscription_payload) => {
    const uri = localStorage.getItem("redirect_uri");
    const account_linking_token = localStorage.getItem("account_linking_token");
    window.location.href = `${uri}${pageQuery.makeString({
        account_linking_token,
        subscription_payload,
    })}`;
};

// remove the modal
const removeModal = () => {
    const modal = document.querySelector(".tp-modal");
    const articleModal =
    document.querySelectorAll('[data-page-type="article"], body.cma, .tp-modal').
        length >= 2;
    if (articleModal) {
        const backdrop = document.querySelector(".tp-backdrop.tp-active");
        modal.parentElement.removeChild(modal);
        backdrop.parentElement.removeChild(backdrop);
        document.body.classList.remove("tp-modal-open");
    }
};

const tinyPassUrl = {
    dev: `https://sandbox.tinypass.com/xbuilder/experience/load?aid=${pianoKey}`,
    prod: `https://experience.tinypass.com/xbuilder/experience/load?aid=${pianoKey}`,
}[locationDetails.tier.match("prod") ? "prod" : "dev"];

const initPiano = () => {
    PianoAdBlock(); // init piano's ad block
    // detect private mode add variables to to tp
    isPrivateMode().then( (bool) => {
        if (bool) {
            window.tp.push([ "setCustomVariable", "incognito", "true" ]);
            logPrivateMode();
        }
    });

    if (window.adiData) {
    // log page details to tp
        window.tp.push([ "setContentSection", window.adiData.blogId ]);
        window.tp.push([ "setContentCreated", window.adiData.entryDateOriginal ]);

        if (window.adiData.entryAuthorUsername) {
            window.tp.push([
                "setContentAuthor",
                window.adiData.entryAuthorUsername.toLowerCase()
            ]);
        }

        if (window.adiData.pageType !== undefined) {
            window.tp.push([
                "setCustomVariable",
                "pagetype",
                window.adiData.pageType,
                "content"
            ]);
        }

        if (window.adiData.entryTags) {
            window.tp.push([
                "setTags",
                window.adiData.entryTags.toLowerCase().split(";")
            ]);
        }
    }

    window.tp.push([ "setUseGigyaUserProvider", true ]);

    window.tp.push([
        "init",
        function () {
            window.tp.gigya.init({
                apiKey: gigya.thisScript.APIKey,
                screenSet: "ADI-RegistrationLogin",
                startScreen: "gigya-register-screen",
            });

            window.window.tp.enableGACrossDomainLinking();
            window.tp.experience.init();
        }
    ]);
};

// redirect to facebook
const FBRedirect = (results) =>
    results.response &&
  results.subscription_payload &&
  redirectToFB(results.subscription_payload);

const FBReload = () => {
    const alt = pageQuery.getValue("account_linking_token");

    // ask to login via the 'ADI-RegistrationLogin' screenset
    if (pageQuery.getValue("fb") && fbEnabled) {
        window.gigya.accounts.showScreenSet({
            screenSet: "ADI-RegistrationLogin",
            containerID: "gigya-login-container",
        });
    }
    // if there'se an account_linking_token, set some stuff in the localhost and redirect
    if (alt) {
        localStorage.setItem("account_linking_token", alt);
        localStorage.setItem("redirect_uri", pageQuery.getValue("redirect_uri") );
        window.location.href = `${window.location.origin +
      window.location.pathname}?fb=true`;
    }
};

let loaded = false;

// load piano
const loader = () => {
    loadScript(tinyPassUrl).
        then( () => {
            if (!loaded) {
                initPiano();
            }
            loaded = true;
        }).
        catch( (e) => console.error(e) );
};

/*
 * parse out post messages from piano.
 * set up analytics with data gotten received from checkout flow
 * determine if we're a landing page or not.
 * this code is pulled out for unit tests since postMessage doesnt work with origin.
 * note that the double catch block is to surpress json errors but not code / data errors from pianoCallbacks.
 * { Object } event - event object from post message
 */
const handlePianoPostMessage = (event) => {
    try {
        const data = JSON.parse(event.data);
        try {
            data.alreadySubscribed && tp.experience.execute();

            data.piano && data.analytics && pianoCallbacks(data); // data is not for analytics

            data.piano && data.piano.offerType && (state.offerType = data.piano.offerType);
        } catch (e) {
            callCaleLogError({handlePianoPostMessage: e.message});
        }
    } catch (e) {
    // disregard if it's not json, this isn't an error.
    }
};

/*
 * parse out post messages from ProjectText.
 * send a fetch request to cale
 * this code is pulled out for unit tests since postMessage doesnt work with origin.
 * { Object } event - event object from post message
 */
const handleProjectTextPostMessage = (event) => {
    const campaignId = document.querySelector("[data-campaign-id]").dataset.
        campaignId;
    getUser().
        then( () => callCaleTextSubscribe(event.data, campaignId) ).
        catch( (e) => callCaleLogError({projectText: e.message}) );
};

const checkFBResults = (results) =>
    results.response &&
  results.subscription_payload &&
  redirectToFB(results.subscription_payload);

const setupListeners = () => {
    window.tp = window.tp || [];

    setupAnalyticsListeners();

    window.addEventListener("getAccountInfo.adi", () => {

        // deal with gigya/piano auth lag when user logs in
        refreshAfterLogin && setTimeout( () => {
            tp.offer.close();
            tp.experience.execute();
            refreshAfterLogin = false;
        }, 100);

        callCaleAccess().
            then( (results) => {
                checkFBResults(results);
                results.response && removeModal();
                loader();
            }).
            catch( () => {
                loader();
            });
    });

    window.addEventListener("noUser.adi", loader); // if no user auto logged on from a cookie

    const notLoggedIn = window.addEventListener("notLoggedIn.adi", () => {
        window.removeEventListener("notLoggedIn.adi", notLoggedIn);
        getUser().
            then( (results) => callCaleAccess(results) ).
            then(FBRedirect).
            finally(loader); // maybe auth token expired.  try again.
    });

    window.tp.push([
        "addHandler",
        "checkoutComplete",
        () => {
            getUser().
                then( (results) => callCaleEntitlements(results) ).
                then(FBRedirect).
                catch( (e) => callCaleLogError({checkoutComplete: e.message}) );
        }
    ]);

    window.tp.push([
        "addHandler",
        "customEvent",
        (event) => {
            if (event.eventName === "logout") {
                tp.gigya.logout();
            }
        }
    ]);

    // deal with gigya/piano auth lag
    window.addEventListener("onLogout.adi", () => {
        state.progression && setTimeout( () => {
        // bug in piano that user cannot log back in until after reload
            window.location.reload();
        }, 100);
    });

    window.tp.push([
        "addHandler",
        "checkoutClose",
        (event) => {
            if (event.state === "close" && document.getElementById(landingPageId) ) {
                window.location.href = window.location.origin;
            }
        }
    ]);

    // listen for toprail
    window.addEventListener("externalLogin.adi", () => {
        refreshAfterLogin = true;
    });

    window.addEventListener("message", (event) => {
        if (event.origin === "https://projecttext.com") {
            handleProjectTextPostMessage(event);
        }
    });

    window.addEventListener(
        "message",
        (event) => {
            if (event.origin.match(/tinypass.com$/) ) {
                handlePianoPostMessage(event);
            }
        },
        false
    );

    // sometimes we want to refresh after we login, so instead of calling logUserIn(), call the external event instead
    window.tp.push([
        "addHandler",
        "customEvent",
        (event) => {
            if (event.eventName === "loginAndRefresh") {
                tp.gigya.login();
                refreshAfterLogin = true;
            }
        }
    ]);
};

// init the application ( if we're piano enabled )
const setup = () => {
    FBReload();
    setupListeners();
};

const init = () => {
    if (pianoEnabled) {
        if (window.Advance && window.Advance.Gigya && window.Advance.Gigya.ready) {
            setup();
        } else {
            window.addEventListener("gigyaReady.adi", setup);
        }
    }
};

init();

export {handlePianoPostMessage, handleProjectTextPostMessage};

